/**
 * Capability-aware gating for sidebar nav items. The intent is "flexible":
 * each NavItem declares its own `requireAnyCapability` list (any-of OR-match,
 * plus the `full_access` wildcard), so future items can change their gate by
 * editing the array in [Workspace.tsx](src/shell/Workspace.tsx) — no helper
 * surgery required.
 */
import type { CapabilityMap } from '../lib/api-client';

import type { NavItem } from './types';

/**
 * True if `item` should be visible to a user with these `capabilities`.
 *   - No `requireAnyCapability` on the item → visible to everyone.
 *   - `full_access === true` → wildcard, visible regardless of the list.
 *   - Otherwise: at least one of the required caps must be true.
 *
 * Treats a null/undefined capability map as "empty" (no gated nav items).
 */
export function canSeeNavItem(
  item: NavItem,
  capabilities: CapabilityMap | null | undefined,
): boolean {
  if (!item.requireAnyCapability || item.requireAnyCapability.length === 0) return true;
  if (!capabilities) return false;
  if (capabilities.full_access === true) return true;
  for (const cap of item.requireAnyCapability) {
    if (capabilities[cap] === true) return true;
  }
  return false;
}

export function filterNavItems(
  items: NavItem[],
  capabilities: CapabilityMap | null | undefined,
): NavItem[] {
  return items.filter((item) => canSeeNavItem(item, capabilities));
}

/**
 * Custom cell mode (deployment-level flag, see runtime-config
 * `isCustomCellMode`): the console trims to the custom-cell operational
 * surface — runs/logs/traces, integration keys, and the admin views that
 * reflect what auto-provisioning creates. NAV ids drive the sidebar and the
 * command palette; VIEW ids additionally include detail views that tabs may
 * hold even though no nav item points at them.
 */
export const CUSTOM_CELL_NAV_IDS: ReadonlySet<string> = new Set([
  'sandbox-runtime-logs',
  'api-keys',
  // Credentials is the centrepiece of egress credential vaulting: registering
  // an alias, binding it to destinations and scoping it to a tenant all happen
  // here, so trimming it away leaves the feature unusable in custom cell mode.
  //
  // Repositories stays hidden deliberately. The tenant-owned skill-repo path is
  // deferred, and showing a view for machinery that is not wired would be worse
  // than not showing it.
  // The Proxy Vault is where a custom-cell deployment registers a vendor
  // connection, binds its values and watches the traffic. Trimming it away
  // leaves egress credential vaulting unusable, which is the same reason
  // Credentials is on this list.
  'proxy-vault',
  'git-credentials',
  // The Skills Repository is the integration's skill intake (uploaded bundles
  // bound per conversation, PLAN-SKILLS-REPOSITORY-2026-08-26) — a custom-cell
  // deployment is exactly where integrations upload, so the operator must be
  // able to see and manage what they published.
  'skills-repository',
  // The Sandbox Fleet + SDKs pair IS the custom-cell operator story: which
  // bundles serve turns, from which registries, on which SDK versions.
  'sandbox-fleet',
  'sdk-governance',
  // Operator SFTP onto tenant workspaces: a custom-cell deployment is exactly where
  // operators push harness files, so keys, the allowlist and the connection card must
  // stay reachable there — none of it needs a deployment any more.
  'sftp',
  // The catalog behind the credential form's template picker. Without it,
  // custom-cell deployments could USE templates but never add a vendor.
  'tenants',
  'users',
  'roles',
]);

export const CUSTOM_CELL_VIEW_IDS: ReadonlySet<string> = new Set([
  ...CUSTOM_CELL_NAV_IDS,
  'sandbox-runtime-detail',
  // Pool tabs open from the Fleet list; restored tabs must render too.
  'sandbox-fleet-pool',
  // A connection tab is opened from the Proxy Vault list, so no nav item points
  // at it — but a tab restored from storage still has to be allowed to render.
  'proxy-vault-connection',
]);

/** Identity when disabled; allowlist intersection (order-preserving) when on. */
export function applyCustomCellMode(items: NavItem[], enabled: boolean): NavItem[] {
  if (!enabled) return items;
  return items.filter((item) => CUSTOM_CELL_NAV_IDS.has(item.id));
}
